import { z } from "zod";
import { NormalizedEmail } from "../email";
import { VALID_TIMEZONES } from "../timezone";

// `aboutMeContentJson` lives on `users.updateAboutMe` instead of here. Putting
// the recursive TipTapDocSchema directly on a form-validating Zod schema
// breaks `useForm`'s StandardSchema inference (Zod widens the recursive
// content array to `unknown[]`). Keeping the rich-text path on a sibling
// mutation avoids the issue and lets navigator continue submitting plaintext
// `aboutMe` here unchanged.
const ProfileUpdateSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    organization: z.string().max(100, "Organization must be 100 characters or less").optional(),
    email: NormalizedEmail,
    bio: z.string().optional(),
    aboutMe: z.string().nullish(),
    interests: z.string().max(1000, "Interests must be 1000 characters or less").nullish(),
    twoFactorEnabled: z.boolean(),
    calendarTimeZone: z
        .string()
        .refine((tz) => VALID_TIMEZONES.includes(tz), { message: "Invalid timezone" })
        .nullish(),
});

export const schema = {
    getProfile: {
        input: z.void(),
        output: ProfileUpdateSchema,
    },

    updateProfile: {
        input: ProfileUpdateSchema,
        output: z.void(),
    },

    updateEmail: {
        input: z.object({
            email: NormalizedEmail,
        }),
        output: z.object({
            token: z.string(),
        }),
    },

    updateBio: {
        input: z.object({
            bio: z.string().max(500, "Bio must be 500 characters or less").optional(),
        }),
        output: z.void(),
    },

    updateOrganization: {
        input: z.object({
            organization: z.string().max(100, "Organization must be 100 characters or less"),
        }),
        output: z.void(),
    },

    generateTwoFactor: {
        input: z.void(),
        output: z.object({
            qrCodeDataURL: z.string(),
            otpauthUrl: z.string(),
        }),
    },

    verifyTwoFactor: {
        input: z.object({
            code: z.string().length(6),
        }),
        output: z.object({
            success: z.boolean(),
        }),
    },

    disableTwoFactor: {
        input: z.void(),
        output: z.void(),
    },
};
